// ============================================================================
// Time & Labor — Payroll & reports (package three, authorized 2026-08-28).
//
// Three generators, each matched to the REAL artifact it replaces:
//   Sub pay sheets   — the per-facility weekly grids Antonela emails each sub
//                      (print → PDF replicates today's attachments; CSV too).
//   Payroll Summary  — the semimonthly W2 + 1099 workbook, plus the Gusto
//                      Hours CSV (D-11: we generate the file, Amy uploads —
//                      Gusto owns rates, so its CSV carries 0.00 on purpose).
//   1099 list        — the email Jose gets; "Copy email body" and paste.
//
// ⛔ D-05 GOVERNS EVERY DOLLAR HERE: an unverified rate renders hours and
//    never money. The database enforces it; this screen just doesn't lie.
// ⛔ D-01: the OT columns exist for workbook parity and are structurally 0.
// Every generate writes an immutable period_exports row (TLX-): regenerating
// mints the next version and names what it supersedes. Nothing here sends —
// Antonela prints/downloads and emails from her own inbox.
// ============================================================================

import { canRunPayAndReports } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { fmtWhen } from './console-parts';
import { useTimeTabsNav } from './time-console';
import { ExportButtons } from './export-buttons';
import { InOutActivityReport } from './inout-activity-report';
import { downloadCsv, type CellValue } from './export-file';
import { SmartSelect, isAssignableSub, type SmartSelectRow } from '../../components/smart-select';
import {
  generateContractor1099,
  generatePayrollSummary,
  generatePaysheets,
  useExportHistory,
  useSubPickerGroups,
} from './hooks/use-time-data';
import type {
  Contractor1099Payload,
  PayrollSummaryPayload,
  PaysheetFacility,
  PaysheetPayload,
  PaysheetSegment,
} from './types';
import { formatSiteTime } from '../../lib/siteTime';

// ---------------------------------------------------------------------------
// Small format helpers. A clock time is on the WORKER's clock — the building's
// market — and says so ("7:52 PM CT"), so a sheet generated from any laptop,
// in any country, matches the one the sub already knows (Ben, 2026-09-24).
// ---------------------------------------------------------------------------
function fmtTime(iso: string | null, tz: string | null): string {
  if (!iso) return '—';
  return formatSiteTime(iso, tz);
}

function fmtDay(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
}

function money(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function hoursHM(h: number): string {
  const mins = Math.round(h * 60);
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

/** Most recent Sunday (a pay-sheet week ends on Sunday). */
function lastSunday(): string {
  const d = new Date();
  d.setDate(d.getDate() - (d.getDay() === 0 ? 7 : d.getDay()));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function weekDates(weekStart: string): string[] {
  const [y, m, d] = weekStart.split('-').map(Number);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(y, m - 1, d + i);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  });
}

const EXPORT_TYPE_LABELS: Record<string, string> = {
  sub_pay_sheet: 'Sub payment sheet',
  payroll_summary: 'Payroll Summary',
  contractor_1099_list: '1099 list',
  client_audit: 'Client audit',
};

// ---------------------------------------------------------------------------
// Pay-sheet facility card — the grid a sub already recognizes.
// ---------------------------------------------------------------------------
function PaysheetCard({ fac, weekStart, provider }: { fac: PaysheetFacility; weekStart: string; provider: string }) {
  const days = weekDates(weekStart);
  const byWorkerDay = (segments: PaysheetSegment[]) => {
    const m = new Map<string, PaysheetSegment[]>();
    for (const s of segments) {
      const arr = m.get(s.date) ?? [];
      arr.push(s);
      m.set(s.date, arr);
    }
    return m;
  };
  return (
    <div className="print-page mt-4 overflow-x-auto rounded-xl border border-tcs-border bg-tcs-raised p-4">
      <div className="text-xs font-bold uppercase tracking-wider text-tcs-text-muted">Terminal Cleaning Services</div>
      <div className="text-base font-bold text-tcs-text">{provider} — {fac.place_display}</div>
      <table className="mt-2 w-full border-collapse text-xs">
        <thead>
          <tr className="bg-tcs-surface text-left text-[10.5px] uppercase tracking-wide text-tcs-text-muted">
            <th className="px-2 py-1.5 font-semibold">Employee</th>
            {days.map((d) => <th key={d} className="px-2 py-1.5 text-center font-semibold">{fmtDay(d)}</th>)}
            <th className="px-2 py-1.5 text-right font-semibold">Hours</th>
            <th className="px-2 py-1.5 text-right font-semibold">Rate</th>
            <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {fac.workers.map((w) => {
            const perDay = byWorkerDay(w.segments);
            return (
              <tr key={w.worker_id} className="border-t border-tcs-border align-top">
                <td className="px-2 py-1.5 font-medium text-tcs-text">{w.worker_name}</td>
                {days.map((d) => {
                  const segs = perDay.get(d) ?? [];
                  return (
                    <td key={d} className="px-2 py-1.5 text-center tabular-nums">
                      {segs.length === 0 ? <span className="text-tcs-text-muted">—</span> : segs.map((s, i) => (
                        <div key={i}>
                          <div>{fmtTime(s.in, s.site_tz)}–{fmtTime(s.out, s.site_tz)}</div>
                          <div className="text-tcs-text-muted">{(s.minutes / 60).toFixed(2)} h</div>
                        </div>
                      ))}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{w.week_hours.toFixed(3)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {w.rate != null ? money(w.rate) : <span className="text-st-warn-fg">unverified</span>}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{w.amount != null ? money(w.amount) : '—'}</td>
              </tr>
            );
          })}
          <tr className="border-t-2 border-tcs-border-strong bg-tcs-surface font-semibold">
            <td className="px-2 py-1.5">Total — {fac.place_display}</td>
            <td className="px-2 py-1.5" colSpan={7} />
            <td className="px-2 py-1.5 text-right tabular-nums">{fac.facility_hours.toFixed(3)}</td>
            <td className="px-2 py-1.5" />
            <td className="px-2 py-1.5 text-right tabular-nums">
              {fac.total_due != null ? money(fac.total_due) : <span className="font-normal text-st-warn-fg">hours only — a rate is unverified</span>}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function TimeReports() {
  const { profile } = useAuth();
  const tabsNav = useTimeTabsNav('reports');
  const history = useExportHistory();

  const role = profile?.role ?? 'none';
  const canGenerate = canRunPayAndReports(role);

  // ----- sub pay sheets -----
  const [psProvider, setPsProvider] = useState('');
  const [psWeekEnding, setPsWeekEnding] = useState(lastSunday());
  const [psBusy, setPsBusy] = useState(false);
  const [psError, setPsError] = useState<string | null>(null);
  const [paysheet, setPaysheet] = useState<PaysheetPayload | null>(null);

  // ----- payroll summary + 1099 (share the range) -----
  const [rStart, setRStart] = useState('');
  const [rEnd, setREnd] = useState('');
  const [sumBusy, setSumBusy] = useState(false);
  const [sumError, setSumError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PayrollSummaryPayload | null>(null);
  const [nnBusy, setNnBusy] = useState(false);
  const [nnError, setNnError] = useState<string | null>(null);
  const [list1099, setList1099] = useState<Contractor1099Payload | null>(null);
  const [copied, setCopied] = useState(false);

  async function runPaysheets() {
    setPsBusy(true); setPsError(null);
    try {
      setPaysheet(await generatePaysheets(psProvider, psWeekEnding));
      await history.reload();
    } catch (e) {
      setPsError(errorMessage(e, 'Could not build the pay sheets.'));
    } finally { setPsBusy(false); }
  }

  async function runSummary() {
    setSumBusy(true); setSumError(null);
    try {
      setSummary(await generatePayrollSummary(rStart, rEnd));
      await history.reload();
    } catch (e) {
      setSumError(errorMessage(e, 'Could not build the summary.'));
    } finally { setSumBusy(false); }
  }

  async function run1099() {
    setNnBusy(true); setNnError(null);
    try {
      setList1099(await generateContractor1099(rStart, rEnd));
      await history.reload();
    } catch (e) {
      setNnError(errorMessage(e, 'Could not build the 1099 list.'));
    } finally { setNnBusy(false); }
  }

  // ⛔ Built ONCE and handed to every format, so the Excel file and the CSV
  // can never disagree about what is in the report.
  function paysheetRows(): CellValue[][] {
    if (!paysheet) return [];
    const rows: (string | number | null)[][] = [
      [`${paysheet.provider_display} — week ${paysheet.week_start} to ${paysheet.week_ending}`],
      ['Facility', 'Employee', 'Date', "In (worker's time)", "Out (worker's time)", 'Hours', 'Week hours', 'Rate', 'Amount'],
    ];
    for (const fac of paysheet.facilities) {
      for (const w of fac.workers) {
        for (const s of w.segments) {
          rows.push([fac.place_display, w.worker_name, s.date, fmtTime(s.in, s.site_tz), fmtTime(s.out, s.site_tz),
            (s.minutes / 60).toFixed(3), w.week_hours.toFixed(3), w.rate ?? '', w.amount ?? '']);
        }
      }
      rows.push([fac.place_display, 'TOTAL', '', '', '', fac.facility_hours.toFixed(3), '', '', fac.total_due ?? '']);
    }
    return rows;
  }

  function summaryRows(): CellValue[][] {
    if (!summary) return [];
    const rows: (string | number | null)[][] = [
      [`TCS Payroll — ${summary.range_start} to ${summary.range_end}`],
      ['Section', 'Employee', 'Location', 'Reg Hours', 'OT Hours', 'Hours Worked', 'Wage/Hr', 'OT Wage', 'Reg Pay', 'OT Pay', 'Total Pay'],
      ...summary.rows.map((r) => [r.section, r.worker_name, r.location, r.reg_hours, r.ot_hours,
        r.reg_hours, r.wage ?? '', r.ot_wage ?? '', r.reg_pay ?? '', 0, r.total_pay ?? '']),
      ['GRAND TOTAL', '', '', '', '', summary.hours_total, '', '', '', '', summary.amount_total ?? ''],
    ];
    return rows;
  }

  function gustoRows(): CellValue[][] {
    if (!summary) return [];
    const [ys, ms, ds] = summary.range_start.split('-');
    const [ye, me, de] = summary.range_end.split('-');
    const rows: (string | number)[][] = [
      [`Pay Period: ${Number(ms)}/${Number(ds)}/${ys} - ${Number(me)}/${Number(de)}/${ye}`, '', '', '', '', '', '', '', '', ''],
      ['Employee Id', 'First Name', 'Last Name', 'Earning Code', 'Hours', 'Hours & Minutes', 'Location', 'Department Code', 'Rate', 'Pay Amount'],
      ...summary.gusto_rows.map((g) => ['', g.first_name, g.last_name, g.earning_code,
        g.hours.toFixed(3), hoursHM(g.hours), g.location, g.department, '0.00', '0.00']),
    ];
    return rows;
  }

  async function copy1099Email() {
    if (!list1099) return;
    const names = list1099.rows.map((r) => `${r.worker_name}${r.is_new ? ' (New)' : ''}`).join('\n');
    const table = list1099.rows.map((r) =>
      `${r.worker_name}\t${r.location}\t${r.hours.toFixed(3)}\t${r.wage != null ? money(r.wage) : 'rate unverified'}\t${r.total_pay != null ? money(r.total_pay) : '—'}`).join('\n');
    const body = [
      'Hi,',
      '',
      'Below is the list of workers to be paid as 1099 contractors for this period.',
      'For new contractors, their bank account information is in the app.',
      '',
      names,
      '',
      `TCS Payroll — ${list1099.range_start} to ${list1099.range_end}`,
      'Employee\tLocation\tRegular Hours\tWage per Hr.\tTotal Pay',
      table,
      '',
      `Total hours: ${list1099.hours_total.toFixed(3)}${list1099.amount_total != null ? ` · Total pay: ${money(list1099.amount_total)}` : ''}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setNnError('Could not reach the clipboard — select the table and copy it by hand.');
    }
  }

  // ----- the sub picker's groups -----
  const picker = useSubPickerGroups(psWeekEnding);

  /**
   * Who may be CHOSEN. ⛔ Two kinds of row, one rule each, and neither filters
   * the fetch: a row that cannot be picked is still listed, and says why.
   *  - a person: pickable only once their employer is recorded, because a pay
   *    sheet is per sub and there is nothing to build one from otherwise.
   *  - a sub: the standing predicate — an Active field sub, or one mid-
   *    onboarding with a live record. A sub we no longer use is out.
   */
  const pickerSelectable = useMemo(
    () => (row: SmartSelectRow) =>
      (row.raw?.kind === 'person' ? Boolean(row.raw.provider_id) : isAssignableSub(row)),
    [],
  );

  const pickerGroups = useMemo(() => {
    const groups: Array<{ key: string; label: string; rows: SmartSelectRow[] }> = [];
    if (picker.worked.length > 0) {
      groups.push({
        key: 'worked',
        label: `Subs with hours in the week to ${fmtDay(psWeekEnding)}`,
        rows: picker.worked.map((w) => ({
          id: w.provider_id,
          name: w.provider_name,
          subtitle: `${w.people} ${w.people === 1 ? 'person' : 'people'} · ${w.buildings} ${w.buildings === 1 ? 'building' : 'buildings'} this week`,
          raw: { kind: 'sub', assignable: true } as unknown as Record<string, unknown>,
        })),
      });
    }
    groups.push({
      key: 'people',
      label: 'People',
      rows: picker.people.map((p) => ({
        // ⛔ Picking a PERSON means picking THEIR SUB — that is what a pay
        // sheet is. The row therefore carries the sub's id.
        id: p.provider_id ?? `WRK:${p.worker_id}`,
        name: p.name,
        subtitle: p.provider_name
          ? `works for ${p.provider_name}`
          : 'no sub recorded for them yet — that is the register Antonela confirms after October',
        raw: { kind: 'person', provider_id: p.provider_id } as unknown as Record<string, unknown>,
      })),
    });
    return groups;
  }, [picker.people, picker.worked, psWeekEnding]);

  /** How many people are recorded as working for the chosen sub. */
  const rosterSize = useMemo(
    () => (psProvider === '' ? 0 : picker.people.filter((p) => p.provider_id === psProvider).length),
    [picker.people, psProvider],
  );

  const summarySections = useMemo(() => {
    if (!summary) return [];
    return (['W2', '1099'] as const).map((sec) => ({
      sec,
      rows: summary.rows.filter((r) => r.section === sec),
    })).filter((s) => s.rows.length > 0);
  }, [summary]);

  if (!canGenerate) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-6">
        <PageHeader eyebrow="Time & Labor" title="Payroll & reports" meta="HR, finance and executives generate payroll artifacts." />
        <div className="mt-3"><TabBar {...tabsNav} /></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <style>{`@media print {
        nav, header, .no-print { display: none !important; }
        .print-page { break-after: page; border: none !important; }
      }`}</style>
      <div className="no-print">
        <PageHeader
          eyebrow="Time & Labor"
          title="Payroll & reports"
          meta="Every generate is recorded (TLX-) and versioned. An unverified rate shows hours and never dollars — verify rates on Workers & caps. Nothing here sends; print or download, then email it yourself."
        />
        <div className="mt-3"><TabBar {...tabsNav} /></div>
      </div>

      {/* ===== Sub pay sheets ===== */}
      <section className="no-print mt-5 rounded-xl border border-tcs-border bg-tcs-raised p-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-tcs-text-muted">Sub payment sheets — weekly (Mon–Sun)</h2>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          {/* ⛔ THE SEARCHABLE PICKER, not a second one (TCSD-0171). The list
              this replaces offered all 111 subs in internal-record-number
              order with no search box, so finding one meant scrolling. */}
          <div className="w-[300px] max-w-full">
            <SmartSelect
              entity="service_provider"
              entityLabel="sub"
              placeholder="Search a sub or a person…"
              value={psProvider === '' ? null : psProvider}
              onChange={(id) => setPsProvider(id)}
              isSelectable={pickerSelectable}
              extraGroups={pickerGroups}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="ps-we">Week ending (Sunday)</label>
            <input id="ps-we" type="date" value={psWeekEnding} onChange={(e) => setPsWeekEnding(e.target.value)}
              className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text" />
          </div>
          <Button size="sm" loading={psBusy} disabled={psProvider === '' || psWeekEnding === ''} onClick={() => void runPaysheets()}>
            Generate pay sheets
          </Button>
          {paysheet && (
            <ExportButtons
              baseFilename={`paysheets-${paysheet.provider_display.replace(/\W+/g, '-')}-WE-${paysheet.week_ending}`}
              sheetName={`Pay sheet WE ${paysheet.week_ending}`}
              rows={paysheetRows}
            />
          )}
        </div>
        {psError && <div className="mt-2 text-sm text-st-danger-fg">{psError}</div>}
        {picker.error && <div className="mt-2 text-sm text-st-danger-fg">{picker.error}</div>}
        {paysheet && (
          <div className="mt-2 text-xs text-tcs-text-secondary">
            {paysheet.export.export_id} · v{paysheet.export.version}
            {paysheet.export.supersedes && ` · supersedes ${paysheet.export.supersedes}`}
            {' · '}{paysheet.hours_total.toFixed(3)} h
            {paysheet.amount_total != null && ` · ${money(paysheet.amount_total)} due`}
            {(paysheet.warnings.unresolved_autocloses > 0 || paysheet.warnings.still_open > 0) && (
              <span className="ml-2 rounded-full border border-st-warn-border bg-st-warn-bg px-2 py-0.5 font-semibold text-st-warn-fg">
                {paysheet.warnings.unresolved_autocloses} unresolved auto-close{paysheet.warnings.unresolved_autocloses === 1 ? '' : 's'}
                {paysheet.warnings.still_open > 0 && ` · ${paysheet.warnings.still_open} still open`} — settle before sending
              </span>
            )}
          </div>
        )}
      </section>
      {paysheet && paysheet.facilities.map((fac) => (
        <PaysheetCard key={fac.place_key} fac={fac} weekStart={paysheet.week_start} provider={`${paysheet.provider_display}'s Team`} />
      ))}
      {/* ⛔ AN EMPTY SHEET SAYS WHICH EMPTY IT IS. "Nobody on this roster worked
          this week" and "nobody is recorded as working for this sub at all" are
          opposite problems with identical-looking output, and only the second
          is waiting on the register. ⛔ Derived from the roster, never from a
          hardcoded flag or a date. */}
      {paysheet && paysheet.facilities.length === 0 && (
        <div className="mt-3 rounded-xl border border-tcs-border bg-tcs-raised px-4 py-6 text-center text-sm">
          {rosterSize > 0 ? (
            <>
              <div className="font-semibold text-tcs-text">
                Nobody on this sub&rsquo;s roster worked in the week to {fmtDay(psWeekEnding)}.
              </div>
              <div className="mx-auto mt-1 max-w-prose text-xs text-tcs-text-secondary">
                {rosterSize} {rosterSize === 1 ? 'person is' : 'people are'} recorded as working for
                them; none of them has hours in this week. An ordinary answer, not a fault.
              </div>
            </>
          ) : (
            <>
              <div className="font-semibold text-tcs-text">
                Nobody is recorded as working for this sub at all.
              </div>
              <div className="mx-auto mt-1 max-w-prose text-xs text-tcs-text-secondary">
                That is the register, not this week — who works for which sub is what Antonela
                confirms after October. Until then a pay sheet for them has nothing to build from,
                whatever hours were worked at their buildings.
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== Payroll summary + 1099 ===== */}
      <section className="no-print mt-6 rounded-xl border border-tcs-border bg-tcs-raised p-4">
        <h2 className="text-sm font-bold uppercase tracking-wide text-tcs-text-muted">Payroll Summary & 1099 — pay period</h2>
        <div className="mt-1 text-xs text-tcs-text-secondary">
          The range is free — real periods have not always matched the clean calendar (7/30–8/12 was a real one).
          When the range matches a stored period exactly, the export links to it.
        </div>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="pr-start">From</label>
            <input id="pr-start" type="date" value={rStart} onChange={(e) => setRStart(e.target.value)}
              className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text" />
          </div>
          <div>
            <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="pr-end">Through</label>
            <input id="pr-end" type="date" value={rEnd} onChange={(e) => setREnd(e.target.value)}
              className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text" />
          </div>
          <Button size="sm" loading={sumBusy} disabled={rStart === '' || rEnd === ''} onClick={() => void runSummary()}>
            Generate summary
          </Button>
          <Button size="sm" variant="secondary" loading={nnBusy} disabled={rStart === '' || rEnd === ''} onClick={() => void run1099()}>
            Generate 1099 list
          </Button>
          {summary && (
            <div className="flex flex-wrap items-center gap-4">
              <ExportButtons
                baseFilename={`payroll-summary-${summary.range_start}-to-${summary.range_end}`}
                sheetName="Payroll summary"
                rows={summaryRows}
                hint="the summary"
              />
              {/* ⛔ GUSTO IS CSV AND ONLY CSV. It is a machine format for one
                  named importer, not a report anybody reads, so it keeps its
                  own single button rather than pretending to be three. */}
              <button
                type="button"
                onClick={() => downloadCsv(`gusto-hours-${summary.range_start}-to-${summary.range_end}.csv`, gustoRows())}
                className="no-print rounded-lg border border-tcs-border-strong bg-tcs-bg px-3 py-1.5 text-xs font-semibold text-tcs-text hover:bg-tcs-surface"
              >
                Gusto hours CSV
              </button>
            </div>
          )}
          {list1099 && (
            <Button size="sm" variant="secondary" onClick={() => void copy1099Email()}>
              {copied ? 'Copied ✓' : 'Copy 1099 email body'}
            </Button>
          )}
        </div>
        {(sumError || nnError) && <div className="mt-2 text-sm text-st-danger-fg">{sumError ?? nnError}</div>}
      </section>

      {summary && summarySections.map(({ sec, rows }) => (
        <div key={sec} className="mt-4 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">{sec === 'W2' ? 'W2 employees' : '1099 contractors'}</th>
                <th className="px-3 py-2 font-semibold">Location</th>
                <th className="px-3 py-2 text-right font-semibold">Reg hours</th>
                <th className="px-3 py-2 text-right font-semibold">OT hours</th>
                <th className="px-3 py-2 text-right font-semibold">Wage/hr</th>
                <th className="px-3 py-2 text-right font-semibold">Total pay</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.worker_id}-${i}`} className="border-t border-tcs-border bg-tcs-raised">
                  <td className="px-3 py-2 font-medium text-tcs-text">{r.worker_name}</td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{r.location}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.reg_hours.toFixed(3)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.ot_hours}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.wage != null ? money(r.wage) : <span className="text-st-warn-fg">unverified</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.total_pay != null ? money(r.total_pay) : '—'}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-tcs-border-strong bg-tcs-surface font-semibold">
                <td className="px-3 py-2">{sec} subtotal</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right tabular-nums">{rows.reduce((s, r) => s + r.reg_hours, 0).toFixed(3)}</td>
                <td className="px-3 py-2 text-right tabular-nums">0</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right tabular-nums">
                  {rows.some((r) => !r.rate_verified)
                    ? `${money(rows.reduce((s, r) => s + (r.total_pay ?? 0), 0))} + ${rows.filter((r) => !r.rate_verified).length} hours-only`
                    : money(rows.reduce((s, r) => s + (r.total_pay ?? 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
      {summary && (
        <div className="mt-2 text-xs text-tcs-text-secondary">
          {summary.export.export_id} · v{summary.export.version}
          {summary.export.period_id && ` · linked to ${summary.export.period_id}`}
          {' · grand total '}{summary.hours_total.toFixed(3)} h
          {summary.amount_total != null && ` · ${money(summary.amount_total)} priced`}
        </div>
      )}

      {list1099 && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">1099 contractor</th>
                <th className="px-3 py-2 font-semibold">Location</th>
                <th className="px-3 py-2 text-right font-semibold">Hours</th>
                <th className="px-3 py-2 text-right font-semibold">Wage/hr</th>
                <th className="px-3 py-2 text-right font-semibold">Total pay</th>
              </tr>
            </thead>
            <tbody>
              {list1099.rows.map((r) => (
                <tr key={r.worker_id} className="border-t border-tcs-border bg-tcs-raised">
                  <td className="px-3 py-2 font-medium text-tcs-text">
                    {r.worker_name}
                    {r.is_new && (
                      <span className="ml-2 rounded-full border border-st-info-border bg-st-info-bg px-2 py-0.5 text-[10.5px] font-semibold text-st-info-fg">
                        New — bank info in the app
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{r.location}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.hours.toFixed(3)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.wage != null ? money(r.wage) : <span className="text-st-warn-fg">unverified</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.total_pay != null ? money(r.total_pay) : '—'}</td>
                </tr>
              ))}
              {list1099.rows.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-tcs-text-muted">
                  No 1099 contractor hours in this range.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== In/Out Activity (item 885) — a read; it records nothing ===== */}
      <InOutActivityReport />

      {/* ===== history ===== */}
      <section className="no-print mt-6">
        <h2 className="text-sm font-bold uppercase tracking-wide text-tcs-text-muted">Generated artifacts — immutable record</h2>
        <div className="mt-2 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">Export</th>
                <th className="px-3 py-2 font-semibold">Type</th>
                <th className="px-3 py-2 font-semibold">Label</th>
                <th className="px-3 py-2 font-semibold">Range</th>
                <th className="px-3 py-2 text-right font-semibold">v</th>
                <th className="px-3 py-2 text-right font-semibold">Hours</th>
                <th className="px-3 py-2 text-right font-semibold">Amount</th>
                <th className="px-3 py-2 font-semibold">When</th>
              </tr>
            </thead>
            <tbody>
              {history.rows.map((h) => (
                <tr key={h.export_id} className="border-t border-tcs-border bg-tcs-raised">
                  <td className="px-3 py-2 font-medium text-tcs-text">{h.export_id}</td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{EXPORT_TYPE_LABELS[h.export_type] ?? h.export_type}</td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{h.label ?? '—'}</td>
                  <td className="px-3 py-2 tabular-nums text-tcs-text-secondary">{h.range_start} → {h.range_end}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{h.version}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{h.hours_total != null ? Number(h.hours_total).toFixed(3) : '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{h.amount_total != null ? money(Number(h.amount_total)) : '—'}</td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{fmtWhen(h.created_at)}</td>
                </tr>
              ))}
              {history.rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-tcs-text-muted">
                  Nothing generated yet. The first pay sheet or summary you build lands here forever.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {history.error && <div className="mt-2 text-sm text-st-danger-fg">{history.error}</div>}
      </section>
    </div>
  );
}
